/**
 * ListTable — data table for master entity list.
 * Uses siesa-ui-kit Table with columns derived from fieldEngine.
 * Renders RowActions as last column. Shows loading skeleton when isLoading=true.
 * Column headers with filterable fields show an inline per-column filter input
 * toggled by a small Filter icon button (matches mock .th-filter pattern).
 *
 * @module components/list/ListTable
 * @see FMPV-MVWC-E002-S003 I-002
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table } from '../../../Table/Table';
import type { TableColumn } from '../../../Table/Table.types';
import { ChevronsUpDown, ChevronUp, ChevronDown, Filter, Globe, Building2 } from 'lucide-react';
import type { MasterPatternViewDefinition } from '../../types/definition.types';
import type { MasterPatternViewEntity } from '../../types/service.types';
import type { MasterPatternViewPermissions } from '../../types/permissions.types';
import type { MasterPatternViewCustomAction, MasterPatternViewDefaultActions } from '../../types/props.types';
import { useFieldEngine } from '../../hooks/useFieldEngine';
import { getAllFields } from '../../engine/fieldEngine';
import { useMasterViewStore } from '../../store/MasterViewContext';
import { EmptyState } from './EmptyState';
import { RowActions } from './RowActions';
import { CompanyStatusBadge } from './CompanyStatusBadge';
import type { ToastCallback } from '../../hooks/useToast';

export interface ListTableProps<T extends MasterPatternViewEntity = MasterPatternViewEntity> {
  definition: MasterPatternViewDefinition;
  data: T[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (field: string, direction: 'asc' | 'desc') => void;
  onOpenView: (row: T) => void;
  onOpenEdit: (row: T) => void;
  onChangeStatus?: (row: T) => void;
  isLoading?: boolean;
  permissions?: MasterPatternViewPermissions;
  actions?: MasterPatternViewCustomAction<T>[];
  rowActions?: (row: T) => React.ReactNode;
  toast?: ToastCallback;
  showInternalToasts?: boolean;
  /** Active company context — undefined or 'global' means global view */
  activeCompanyContext?: string;
  /**
   * When provided, a dedicated leftmost column renders a per-row company-enable badge
   * (mirrors MasterCrud's showCompanyStatusColumn) — only shown while a specific company
   * is active. Clicking it (row.isAssigned decides the badge's enabled/not-enabled look)
   * opens the same "Habilitar por compañía" panel as the Acciones-column action does in
   * global scope.
   */
  onCompanyStatusClick?: (row: T) => void | Promise<void>;
  /** When true, disables pointer events on the sticky actions column to prevent interaction through modal overlays */
  modalOpen?: boolean;
  defaultActions?: MasterPatternViewDefaultActions<T>;
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

// Shared class for the inline column filter input / select
const colFilterInputClass =
  'w-full border border-zinc-200 dark:border-zinc-700 rounded-md px-1.5 py-[3px] text-[11px] text-content-primary dark:text-dark-content-primary bg-white dark:bg-zinc-900 outline-none focus:border-blue-300 dark:focus:border-blue-700 focus:ring-2 focus:ring-blue-600/10';

function SortIcon({ field, sortBy, sortOrder }: { field: string; sortBy: string; sortOrder: 'asc' | 'desc' }) {
  if (sortBy !== field) return <ChevronsUpDown size={12} data-testid="icon-chevrons-up-down" className="text-zinc-300 dark:text-zinc-600 flex-shrink-0" />;
  if (sortOrder === 'asc') return <ChevronUp size={12} data-testid="icon-chevron-up" className="text-blue-600 dark:text-blue-400 flex-shrink-0" />;
  return <ChevronDown size={12} data-testid="icon-chevron-down" className="text-blue-600 dark:text-blue-400 flex-shrink-0" />;
}

export function ListTable<T extends MasterPatternViewEntity = MasterPatternViewEntity>({
  definition,
  data,
  sortBy,
  sortOrder,
  onSort,
  onOpenView,
  onOpenEdit,
  onChangeStatus,
  isLoading = false,
  permissions,
  actions,
  rowActions,
  toast: toastProp,
  showInternalToasts = true,
  activeCompanyContext,
  onCompanyStatusClick,
  modalOpen = false,
  defaultActions,
  t,
}: ListTableProps<T>) {
  const tt = useMemo(
    () => t ?? ((key: string, defaultValue?: string) => defaultValue ?? key),
    [t],
  );
  const isCompanyCtx = !!activeCompanyContext && activeCompanyContext !== 'global';
  const { listColumns } = useFieldEngine(definition);

  // Store — column filters live in their own slice, independent of FilterPanel filters.
  // Both are merged in useMasterList before calling service.getAll().
  const storeColumnFilters = useMasterViewStore((s) => s.columnFilters);
  const setStoreColumnFilters = useMasterViewStore((s) => s.setColumnFilters);
  const setSearchParams = useMasterViewStore((s) => s.setSearchParams);

  // Local state: controls the input display value (immediate feedback while typing)
  const [openColumnFilter, setOpenColumnFilter] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  // Ref to avoid stale closure inside the debounce timeout
  const filtersRef = useRef(storeColumnFilters);
  useEffect(() => { filtersRef.current = storeColumnFilters; }, [storeColumnFilters]);

  // Debounce timers per field
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Sync local input state when column filters are cleared externally (e.g. "Limpiar filtros")
  useEffect(() => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const field of Object.keys(prev)) {
        if (prev[field] && !storeColumnFilters.some((f) => f.field === field)) {
          next[field] = '';
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [storeColumnFilters]);

  // Sends the column filter to the backend via the store.
  // Text fields are debounced 350 ms; boolean selects fire immediately.
  const handleColumnFilter = useCallback(
    (field: string, value: string, isBooleanField: boolean) => {
      setColumnFilters((prev) => ({ ...prev, [field]: value }));

      const commit = () => {
        const filterValue: unknown = isBooleanField
          ? value === 'true' ? true : value === 'false' ? false : undefined
          : value;
        const operator = isBooleanField ? 'equals' : 'contains';
        const next = [
          ...filtersRef.current.filter((f) => f.field !== field),
          ...(value ? [{ field, operator, value: filterValue }] : []),
        ];
        setStoreColumnFilters(next);
        setSearchParams({ page: 1 });
      };

      if (isBooleanField) {
        commit();
      } else {
        clearTimeout(debounceRef.current[field]);
        debounceRef.current[field] = setTimeout(commit, 350);
      }
    },
    [setSearchParams],
  );

  const columns = useMemo((): TableColumn<T>[] => {
    const fieldMap = new Map(getAllFields(definition).map((f) => [f.fieldName, f]));

    const dataColumns: TableColumn<T>[] = listColumns.map((col) => {
      const fieldDef = fieldMap.get(col.fieldName);
      const isFilterable = !!(fieldDef?.filterable || fieldDef?.quickFilter);
      // Lookup fields are filterable in the advanced panel but cannot be filtered inline
      // (free-text input against a relational field would silently produce no results).
      const isLookupField = fieldDef?.inputType === 'lookup';
      const isBooleanField = fieldDef?.dataType === 'boolean' || fieldDef?.inputType === 'boolean';
      const isOverridable = !!(fieldDef?.isOverridable);

      let renderFn: ((value: unknown, row: T) => React.ReactNode) | undefined;
      if (fieldDef?.renderCell) {
        const baseRender = (value: unknown, row: T) => fieldDef.renderCell!(value, row) as React.ReactNode;
        renderFn = (value: unknown, row: T) => {
          const base = baseRender(value, row);
          if (!isCompanyCtx || !isOverridable) return base;
          const r = row as Record<string, unknown>;
          const overrides = r['_overrides'] as Record<string, unknown> | undefined;
          const overrideBadge = isOverridable
            ? overrides?.[col.fieldName] !== undefined
              ? <span title={tt('companyOverrideTooltip', 'Valor sobreescrito por la compañía')} className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-blue-50 border border-blue-200 text-blue-700 dark:bg-blue-950/40 dark:border-blue-800 dark:text-blue-400 ml-1 flex-shrink-0 cursor-help"><Building2 size={12} /></span>
              : <span title={tt('companyGlobalValueTooltip', 'Valor global')} className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-400 dark:text-zinc-500 ml-1 flex-shrink-0 cursor-help"><Globe size={12} /></span>
            : null;
          return <span className="inline-flex items-center">{base}{overrideBadge}</span>;
        };
      } else if (isBooleanField) {
        renderFn = (value: unknown, row: T) => {
          const base = value ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-600/10 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300">
              {tt('active', 'Activo')}
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-zinc-200 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
              {tt('inactive', 'Inactivo')}
            </span>
          );
          if (!isCompanyCtx || !isOverridable) return base;
          const r = row as Record<string, unknown>;
          const overrides = r['_overrides'] as Record<string, unknown> | undefined;
          const overrideBadge = isOverridable
            ? overrides?.[col.fieldName] !== undefined
              ? <span title={tt('companyOverrideTooltip', 'Valor sobreescrito por la compañía')} className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-blue-50 border border-blue-200 text-blue-700 dark:bg-blue-950/40 dark:border-blue-800 dark:text-blue-400 ml-1 flex-shrink-0 cursor-help"><Building2 size={12} /></span>
              : <span title={tt('companyGlobalValueTooltip', 'Valor global')} className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-400 dark:text-zinc-500 ml-1 flex-shrink-0 cursor-help"><Globe size={12} /></span>
            : null;
          return <span className="inline-flex items-center">{base}{overrideBadge}</span>;
        };
      } else if (isCompanyCtx && isOverridable) {
        renderFn = (value: unknown, row: T) => {
          const base = value !== undefined && value !== null ? String(value) : '';
          const r = row as Record<string, unknown>;
          const overrides = r['_overrides'] as Record<string, unknown> | undefined;
          const overrideBadge = isOverridable
            ? overrides?.[col.fieldName] !== undefined
              ? <span title={tt('companyOverrideTooltip', 'Valor sobreescrito por la compañía')} className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-blue-50 border border-blue-200 text-blue-700 dark:bg-blue-950/40 dark:border-blue-800 dark:text-blue-400 ml-1 flex-shrink-0 cursor-help"><Building2 size={12} /></span>
              : <span title={tt('companyGlobalValueTooltip', 'Valor global')} className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-400 dark:text-zinc-500 ml-1 flex-shrink-0 cursor-help"><Globe size={12} /></span>
            : null;
          return <span className="inline-flex items-center">{base}{overrideBadge}</span>;
        };
      }

      const isFilterOpen = openColumnFilter === col.fieldName;
      // hasActiveFilter reflects the store (backend filter active), not just the input
      const hasActiveFilter = storeColumnFilters.some((f) => f.field === col.fieldName);

      return {
        header: (
          <div data-testid={`sort-header-${col.fieldName}`}>
            {/* Label row: label on left | filter + sort icons on right */}
            <div
              className="flex items-center justify-between w-full gap-1 cursor-pointer select-none"
              onClick={() => {
                const nextDir = sortBy === col.fieldName && sortOrder === 'asc' ? 'desc' : 'asc';
                onSort(col.fieldName, nextDir);
              }}
            >
              <span className="truncate min-w-0">{col.label}</span>
              <span className="flex items-center gap-0.5 flex-shrink-0">
                {isFilterable && !isLookupField && (
                  <button
                    type="button"
                    data-testid={`filter-icon-${col.fieldName}`}
                    className={`p-0.5 rounded border-0 bg-transparent leading-none transition-colors ${
                      isFilterOpen || hasActiveFilter
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-zinc-400 dark:text-zinc-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenColumnFilter((prev) =>
                        prev === col.fieldName ? null : col.fieldName,
                      );
                    }}
                  >
                    <Filter size={11} />
                  </button>
                )}
                <SortIcon field={col.fieldName} sortBy={sortBy} sortOrder={sortOrder} />
              </span>
            </div>

            {/* Inline filter row — shown when toggled */}
            {isFilterable && !isLookupField && isFilterOpen && (
              <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                {isBooleanField ? (
                  <select
                    autoFocus
                    value={columnFilters[col.fieldName] ?? ''}
                    onChange={(e) => handleColumnFilter(col.fieldName, e.target.value, true)}
                    className={colFilterInputClass}
                    data-testid={`col-filter-${col.fieldName}`}
                  >
                    <option value="">{tt('all', 'Todos')}</option>
                    <option value="true">{tt('active', 'Activo')}</option>
                    <option value="false">{tt('inactive', 'Inactivo')}</option>
                  </select>
                ) : (
                  <input
                    autoFocus
                    type="text"
                    value={columnFilters[col.fieldName] ?? ''}
                    onChange={(e) => handleColumnFilter(col.fieldName, e.target.value, false)}
                    placeholder={tt('filterPlaceholder', 'Filtrar...')}
                    className={colFilterInputClass}
                    data-testid={`col-filter-${col.fieldName}`}
                  />
                )}
              </div>
            )}
          </div>
        ),
        accessor: col.fieldName as keyof T,
        render: renderFn,
      };
    });

    const actionsColumn: TableColumn<T> = {
      header: (
        // The xy header wrapper has `sticky right-0` (position:sticky) → it's a containing block.
        // An absolute child with top:0/bottom:0 fills the full header cell height without negative-margin hacks.
        // data-testid matches the `sort-header-*` selector in master-pattern-view.css so this
        // header cell gets the same grey background as the data-column headers.
        <div className="w-full text-center" data-testid="sort-header-actions">
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '1px',
              backgroundColor: 'var(--color-border-primary)',
              pointerEvents: 'none',
            }}
          />
          {tt('actions', 'Acciones')}
        </div>
      ),
      accessor: 'id' as keyof T,
      align: 'center',
      stickyRight: true,
      // Ancho justo para 3 botones con separación táctil (idéntico a MasterCrudTable.tsx:440-444:
      // 3×28px + 2×gap-1(4px) + 32px de padding de celda ≈ 140px). `fixedWidth` evita que la
      // columna se expanda más de lo necesario.
      width: '140px',
      fixedWidth: true,
      render: (_value: unknown, row: T) => (
        // The md cell wrapper has `sticky right-0` (position:sticky) → it's a containing block.
        // The absolute border div fills top-to-bottom of the cell regardless of row height,
        // eliminating white gaps when rows grow taller than min-h-[56px].
        <>
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '1px',
              backgroundColor: 'var(--color-border-primary)',
              pointerEvents: 'none',
            }}
          />
          <div style={modalOpen ? { pointerEvents: 'none' } : undefined}>
            <RowActions
              row={row}
              permissions={permissions}
              onOpenView={onOpenView}
              onOpenEdit={onOpenEdit}
              onChangeStatus={onChangeStatus}
              actions={actions}
              rowActions={rowActions}
              toast={toastProp}
              showInternalToasts={showInternalToasts}
              defaultActions={defaultActions}
              t={tt}
            />
          </div>
        </>
      ),
    };

    // Dedicated company-status column (mirrors MasterCrud's showCompanyStatusColumn) —
    // only while a specific company is active; in global scope the same panel opens from
    // the "Habilitar compañías" action in the Acciones column instead (see MasterPatternView.tsx).
    const companyStatusColumn: TableColumn<T> | null = isCompanyCtx && onCompanyStatusClick ? {
      header: (
        <span
          data-testid="sort-header-company-status"
          className="inline-flex items-center justify-center w-full text-content-secondary dark:text-dark-content-secondary"
          title={tt('companyStatus', 'Habilitación en la compañía seleccionada')}
        >
          <Building2 size={16} />
        </span>
      ),
      accessor: 'id' as keyof T,
      align: 'center',
      width: '46px',
      fixedWidth: true,
      render: (_value: unknown, row: T) => {
        const enabled = (row as Record<string, unknown>)['isAssigned'] === true;
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onCompanyStatusClick(row); }}
            className="inline-flex"
          >
            <CompanyStatusBadge
              enabled={enabled}
              enabledLabel={tt('enabledInCompany', 'Habilitado en la compañía seleccionada')}
              notEnabledLabel={tt('notEnabledInCompany', 'No habilitado en la compañía seleccionada')}
            />
          </button>
        );
      },
    } : null;

    return companyStatusColumn ? [companyStatusColumn, ...dataColumns, actionsColumn] : [...dataColumns, actionsColumn];
  }, [
    listColumns,
    sortBy,
    sortOrder,
    onSort,
    permissions,
    onOpenView,
    onOpenEdit,
    onChangeStatus,
    actions,
    rowActions,
    isCompanyCtx,
    onCompanyStatusClick,
    toastProp,
    showInternalToasts,
    definition,
    openColumnFilter,
    columnFilters,
    storeColumnFilters,
    handleColumnFilter,
    modalOpen,
    defaultActions,
    tt,
  ]);

  // Pass a styled EmptyState as emptyMessage — the Table's empty-state wrapper (py-12)
  // provides the outer spacing, so compact=true removes EmptyState's own padding.
  // Cast needed because siesa-ui-kit types emptyMessage as string, but React renders
  // any ReactNode correctly.
  const emptyContent = (
    <EmptyState hasSearched compact t={tt} />
  ) as unknown as string;

  return (
    <div className="mpv-list-table">
      <Table<T>
        // key: fuerza un remount cuando entra/sale la columna de compañía (isCompanyCtx).
        // Sin esto, el grid CSS (celdas en display:contents) queda desincronizado del
        // nuevo conteo de columnas — bug conocido del navegador al cambiar la cantidad
        // de hijos de un contenedor display:contents sin remount — y la tabla se ve
        // "escalonada" (cada fila corrida una columna) al pasar de compañía a Global o viceversa.
        key={isCompanyCtx ? 'with-company-col' : 'without-company-col'}
        columns={columns}
        data={data}
        loading={isLoading}
        loadingRows={5}
        // MasterCrud's table has no row-click-to-view behavior — only the explicit
        // Acciones buttons (Visualizar/Editar) open a record. Matched here: no onRowClick.
        variant="fullWidth"
        showBorder={false}
        showShadow={false}
        showPadding={false}
        dense
        emptyMessage={emptyContent}
        data-testid="list-table"
      />
    </div>
  );
}
